import { useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { sound } from "@/lib/sound";

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// HonestPay-themed embedded Stripe onboarding. Renders the real Stripe KYC
// questions INSIDE our app (once, no redirect, no repeat) in a dark modal.
export default function OnboardingModal({ open, onClose, onComplete }) {
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || instance || !PUBLISHABLE_KEY) return;
    const fetchClientSecret = async () => {
      const r = await api.post("/billing/connect/account-session");
      const secret = r.data?.client_secret;
      if (!secret) throw new Error("no client_secret");
      return secret;
    };
    try {
      setInstance(
        loadConnectAndInitialize({
          publishableKey: PUBLISHABLE_KEY,
          fetchClientSecret,
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: "#a855f7",
              colorBackground: "#0e0a16",
              colorText: "#f5f3ff",
              colorSecondaryText: "#a1a1b3",
              colorBorder: "rgba(168,85,247,0.25)",
              colorDanger: "#f87171",
              buttonPrimaryColorBackground: "#7c3aed",
              buttonPrimaryColorText: "#ffffff",
              borderRadius: "14px",
              fontFamily: "Sora, Space Grotesk, sans-serif",
              spacingUnit: "9px",
            },
          },
        })
      );
    } catch {
      setError(true);
    }
  }, [open, instance]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="onboarding-modal"
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[460px] max-h-[88vh] overflow-y-auto hp-glass-strong rounded-3xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-extrabold text-white">Set up payouts</p>
            <p className="text-xs text-muted-foreground">Secure setup by Stripe · about 2 minutes</p>
          </div>
          <button
            data-testid="onboarding-close"
            onClick={onClose}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {error || !PUBLISHABLE_KEY ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Couldn&apos;t start setup right now. Please try again.
          </p>
        ) : instance ? (
          <ConnectComponentsProvider connectInstance={instance}>
            <ConnectAccountOnboarding
              onExit={async () => {
                sound.play("tap");
                try {
                  const r = await api.get("/billing/connect/status");
                  if (r.data?.payouts_enabled) {
                    sound.play("success");
                    toast.success("Payouts ready — your bill is open!");
                  }
                  onComplete?.(r.data);
                } catch {
                  onComplete?.(null);
                }
                onClose();
              }}
            />
          </ConnectComponentsProvider>
        ) : (
          <div className="py-10 grid place-items-center">
            <div className="h-8 w-8 rounded-full border-2 border-[#a855f7]/40 border-t-[#a855f7] animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
